import { useEffect, useRef, useState } from "react";
import { TourDate } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";
import fetchTourDateItinerary from "./use-tour-date-itinerary";

type UseViewPrintProps = {
  selectedTourDate: TourDate | null;
  locale: string;
  marketId: string;
  openModal: () => void;
  closeModal: () => void;
  setStatus: (status: "building" | "ready" | "error") => void;
};

/** Builds a tour itinerary for viewing or printing. */
const useViewPrint = ({
  selectedTourDate,
  locale,
  marketId,
  openModal,
  closeModal,
  setStatus
}: UseViewPrintProps) => {
  const requestIdRef = useRef(0);
  const isInFlightRef = useRef(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  // Leaving the page invalidates any request still in flight so a late result cannot open a tab.
  useEffect(() => () => { requestIdRef.current += 1; }, []);
  const canViewPrint = Boolean(selectedTourDate?.neoTourDate);

  const handleViewPrint = async () => {
    if (!selectedTourDate?.neoTourDate || isInFlightRef.current) return;

    isInFlightRef.current = true;
    setDocumentUrl(null);
    setStatus("building");
    openModal();
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    try {
      const { documentUrl: itineraryUrl } = await fetchTourDateItinerary({
        packageNeoId: selectedTourDate.packageId,
        neoTourDate: selectedTourDate.neoTourDate,
        locale,
        marketId
      });

      if (requestId !== requestIdRef.current) return;

      // "noopener" as a feature makes window.open return null even on success, so detach the
      // opener by hand to keep the blocked-popup detection honest.
      const win = window.open(itineraryUrl, "_blank");

      if (win) {
        win.opener = null;
        closeModal();
      } else {
        setDocumentUrl(itineraryUrl);
        setStatus("ready");
      }
    } catch {
      if (requestId !== requestIdRef.current) return;

      setStatus("error");
    } finally {
      if (requestId === requestIdRef.current) {
        isInFlightRef.current = false;
      }
    }
  };

  const handleClosePrint = () => {
    requestIdRef.current += 1;
    isInFlightRef.current = false;
    setDocumentUrl(null);
    closeModal();
  };

  const handleOpenReady = () => {
    if (!documentUrl) return;

    const win = window.open(documentUrl, "_blank");
    if (!win) return; // blocked again: keep the ready state and its button
    win.opener = null;
    closeModal();
  };

  return { handleViewPrint, handleClosePrint, handleOpenReady, documentUrl, canViewPrint };
};

export default useViewPrint;
